import { useManyCubes } from '@/composables/useManyCubes.ts';
import { useCubeStore } from '@/stores/useCubeStore';
import { useMapStore } from '@/stores/useMapStore';
import { BooleanUtil } from '@/util/BooleanUtil';
import { computed, ref } from 'vue';

export const initialFilters = {
  showDonationRecipients: false,
  showOnlyProducers: false,
  showOnlyProducersWithCapacity: false,
  showOnlyProducersWithGridFeeDiscount: false,
  showOnlyMyFriends: false,
  energyType: 'all',
  minPrice: 0,
  maxPrice: 20,
  searchTerm: '',
}

const showOnlyDonationRecipients = ref(initialFilters.showDonationRecipients)
const showOnlyMyFriends = ref(initialFilters.showOnlyMyFriends)
const showOnlyProducers = ref(initialFilters.showOnlyProducers)
const showOnlyProducersWithCapacity = ref(initialFilters.showOnlyProducersWithCapacity)
const showOnlyProducersWithGridFeeDiscount = ref(initialFilters.showOnlyProducersWithGridFeeDiscount)
const energyType = ref(initialFilters.energyType)
const minPrice = ref(initialFilters.minPrice)
const maxPrice = ref(initialFilters.maxPrice)
const searchTerm = ref(initialFilters.searchTerm)

export function useCubeFilter() {

  const { gridFeeDiscountApplies } = useManyCubes()


  const toggleShowOnlyProducers = () => {
    showOnlyProducers.value = !showOnlyProducers.value
  }

  const toggleShowOnlyProducersWithCapacity = () => {
    showOnlyProducersWithCapacity.value = !showOnlyProducersWithCapacity.value
  }

  const toggleShowOnlyProducersWithGridFeeDiscount = () => {
    showOnlyProducersWithGridFeeDiscount.value = !showOnlyProducersWithGridFeeDiscount.value
  }

  const toggleShowOnlyMyFriends = () => {
    showOnlyMyFriends.value = !showOnlyMyFriends.value
  }

  const toggleShowOnlyDonationRecipients = () => {
    showOnlyDonationRecipients.value = !showOnlyDonationRecipients.value
  }

  const setEnergyType = (newEnergyType) => {
    energyType.value = newEnergyType
  }

  const setMinPrice = (newMinPrice) => {
    minPrice.value = newMinPrice
  }

  const setMaxPrice = (newMaxPrice) => {
    maxPrice.value = newMaxPrice
  }

  const setSearchTerm = (newSearchTerm) => {
    searchTerm.value = newSearchTerm
  }

  const resetFilters = () => {
    showOnlyMyFriends.value = initialFilters.showOnlyMyFriends
    showOnlyProducers.value = initialFilters.showOnlyProducers
    showOnlyProducersWithCapacity.value = initialFilters.showOnlyProducersWithCapacity
    showOnlyProducersWithGridFeeDiscount.value = initialFilters.showOnlyProducersWithGridFeeDiscount
    energyType.value = initialFilters.energyType
    minPrice.value = initialFilters.minPrice
    maxPrice.value = initialFilters.maxPrice
    searchTerm.value = initialFilters.searchTerm
  }

  const showCubeInFilteredList = (cube) => {
    if (showOnlyMyFriends.value) {
      if (!cube.isMyEfriend) {
        // Hide all cubes, that are not connected to the current cube.
        return false
      }
    }

    if (showOnlyDonationRecipients.value) {
      if (!BooleanUtil.isTrue(cube.params.isDonationRecipient)) {
        // Hide all cubes, that are not donation recipients
        return false
      }
    }

    if (showOnlyProducers.value) {
      if (!cube.hasProduction) {
        return false
      }

      if (
        showOnlyProducersWithCapacity.value &&
        (
          // The producer needs to have capacity for this consumer as well.
          cube.params.openConnectionsAvailable < 1 ||
          // We cannot receive energy from ourselves.
          cube.params.nickName === useCubeStore().nickName ||
          // Hide cubes that are already producers for the current cube.
          cube.isProducer
        )
      ) {
        return false
      }

      if (
        showOnlyProducers.value &&
        energyType.value &&
        energyType.value !== 'all' &&
        energyType.value !== cube.productionType
      ) {
        return false
      }

      // Only show cubes that match the current search price constraints.
      const price = useMapStore().getProducerPrices(cube.cubeName)
      if (
        showOnlyProducers.value &&
        price && price.currentPrice &&
        (
          price.currentPrice < minPrice.value / 100 ||
          price.currentPrice > maxPrice.value / 100
        )
      ) {
        return false
      }
    }

    if (
      showOnlyProducersWithGridFeeDiscount.value &&
      !gridFeeDiscountApplies(cube)
    ) {
      return false
    }

    if (searchTerm.value && searchTerm.value !== '') {
      return cube.params.nickName
        .toLowerCase()
        .includes(searchTerm.value.toLowerCase())
    }

    return true
  }

  const numActiveFilters = computed(() => {
    let result = 0
    if (showOnlyMyFriends.value !== initialFilters.showOnlyMyFriends) {
      result++
    }
    if (showOnlyProducers.value !== initialFilters.showOnlyProducers) {
      result++
    }
    if (showOnlyProducersWithCapacity.value !== initialFilters.showOnlyProducersWithCapacity) {
      result++
    }
    if (showOnlyProducersWithGridFeeDiscount.value !== initialFilters.showOnlyProducersWithGridFeeDiscount) {
      result++
    }
    if (energyType.value !== initialFilters.energyType) {
      result++
    }
    if (minPrice.value !== initialFilters.minPrice) {
      result++
    }
    if (maxPrice.value !== initialFilters.maxPrice) {
      result++
    }
    if (searchTerm.value !== initialFilters.searchTerm) {
      result++
    }

    return result
  })

  return {
    initialFilters,
    showCubeInFilteredList,
    showOnlyMyFriends,
    showOnlyDonationRecipients,
    showOnlyProducers,
    showOnlyProducersWithCapacity,
    showOnlyProducersWithGridFeeDiscount,
    energyType,
    minPrice,
    maxPrice,
    searchTerm,
    toggleShowOnlyProducers,
    toggleShowOnlyProducersWithCapacity,
    toggleShowOnlyProducersWithGridFeeDiscount,
    toggleShowOnlyMyFriends,
    toggleShowOnlyDonationRecipients,
    setEnergyType,
    setSearchTerm,
    setMinPrice,
    setMaxPrice,
    resetFilters,
    numActiveFilters,
  }
}

